<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { QuizterBuilder, useQuizterStore } from '@ilteducation/games';
import type { CloseEndedQuestion } from '@ilteducation/exercise-service-types';
import { useLocale, useLocalisation } from '@/plugins/translation';

// Load Quizter translations from the Quizter Lokalise project
const { includeAdditionalProject } = useLocalisation();
includeAdditionalProject('Quizter', false);

const { t } = useLocale();
const router = useRouter();
const quizterStore = useQuizterStore();

const gamesPath = router.resolve({ name: 'Games' }).href;
const absoluteGamesUrl = `${window.location.origin}${gamesPath}`;

const returnParams = computed(() => ({
  url: window.location.href,
  text: t('quiz.back'),
}));

const returnParamsPlayer = computed(() => ({
  url: absoluteGamesUrl,
  text: t('quizter.back_to_games'),
}));

const goBack = () => {
  router.push({ name: 'QuizterHomePage' });
};

const handleStart = async (resourceIds: string[], questions?: CloseEndedQuestion[]) => {
  if (resourceIds.length === 0) {
    return;
  }
  await quizterStore.createGame(resourceIds, false, undefined, questions, returnParams.value, returnParamsPlayer.value);
  const gameId = quizterStore.gameId;
  if (gameId) {
    router.push({ name: 'HostQuizter', params: { gameId } });
  }
};

// Creating persists a new quizter, then hands off to the edit route so further saves update it.
const handleSave = async ({
  resourceIds,
  name,
  description,
  questions,
}: {
  resourceIds: string[];
  name: string;
  description?: string;
  questions?: CloseEndedQuestion[];
}) => {
  const activityId = await quizterStore.saveQuizter({ resourceIds, name, description, questions });
  router.replace({ name: 'QuizterEditPage', params: { activityId }, state: { justSaved: true } });
};
</script>

<template>
  <QuizterBuilder :start="handleStart" :save="handleSave" @back="goBack" />
</template>

<style scoped lang="scss"></style>
